import { useCallback, useEffect, useState } from 'react';

const BOTTOM_TOLERANCE_PX = 2;

function measureScrollElement(element) {
  const { scrollTop, scrollHeight, clientHeight } = element;
  const maxScroll = scrollHeight - clientHeight;
  const hasOverflow = maxScroll > 1;
  const isAtBottom =
    !hasOverflow || Math.abs(maxScroll - scrollTop) <= BOTTOM_TOLERANCE_PX;
  const scrollProgress = hasOverflow
    ? Math.min(1, Math.max(0, scrollTop / maxScroll))
    : 1;

  return {
    scrollProgress,
    isAtBottom,
    hasOverflow,
    showChrome: hasOverflow
  };
}

const INITIAL_SCROLL_STATE = {
  scrollProgress: 0,
  isAtBottom: true,
  hasOverflow: false,
  showChrome: false
};

/**
 * Tracks scroll position for modal body elements (progress bar, fade, hints).
 * @param {React.MutableRefObject} forwardRef - ref assigned to the scroll container
 * @param {unknown[]} deps - remeasure when these change (e.g. wizard step)
 */
export function useModalScrollProgress(forwardRef, deps = []) {
  const [scrollElement, setScrollElement] = useState(null);
  const [state, setState] = useState(INITIAL_SCROLL_STATE);

  const bodyRef = useCallback(
    (node) => {
      setScrollElement(node);
      if (forwardRef) {
        forwardRef.current = node;
      }
    },
    [forwardRef]
  );

  const remeasure = useCallback(() => {
    if (!scrollElement) return;
    setState(measureScrollElement(scrollElement));
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement) return undefined;

    remeasure();

    const handleScroll = () => {
      setState(measureScrollElement(scrollElement));
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      setState(measureScrollElement(scrollElement));
    });

    resizeObserver.observe(scrollElement);
    if (scrollElement.firstElementChild) {
      resizeObserver.observe(scrollElement.firstElementChild);
    }

    const mutationObserver = new MutationObserver(() => {
      setState(measureScrollElement(scrollElement));
    });

    mutationObserver.observe(scrollElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollElement, remeasure, ...deps]);

  return {
    bodyRef,
    ...state,
    remeasure
  };
}
